import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { mediaUrl } from '../lib/media'

type Props = {
  src: string
  /** Optional second source (e.g. m4a for iOS) */
  srcAlt?: string
  title: string
  downloadName?: string
  /** Hide the in-player save when this page does not show it. */
  showSave?: boolean
}

function formatTime(sec: number) {
  if (!Number.isFinite(sec) || sec < 0) return '0:00'
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

/** Restart / go to start */
function IconRestart() {
  return (
    <svg className="studio-ico" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M6 6h2v12H6V6zm3.5 6 8.5 6V6l-8.5 6z"
      />
    </svg>
  )
}

/** Single circular skip (10s) */
function IconSkip10({ dir }: { dir: 'back' | 'fwd' }) {
  const flip = dir === 'fwd' ? 'scale(-1,1)' : undefined
  return (
    <svg className="studio-ico" viewBox="0 0 24 24" aria-hidden="true">
      <g transform={flip ? 'translate(24,0) scale(-1,1)' : undefined}>
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          d="M7.5 8.2A6.8 6.8 0 1 1 5.2 14"
        />
        <path fill="currentColor" d="M7.2 5.2v5.2L3 7.8l4.2-2.6z" />
        <text
          x="12.2"
          y="14.2"
          textAnchor="middle"
          fill="currentColor"
          fontSize="7.5"
          fontWeight="700"
          fontFamily="system-ui,sans-serif"
        >
          10
        </text>
      </g>
    </svg>
  )
}

/** Double circular skip (30s) */
function IconSkip30({ dir }: { dir: 'back' | 'fwd' }) {
  return (
    <svg className="studio-ico" viewBox="0 0 24 24" aria-hidden="true">
      <g transform={dir === 'fwd' ? 'translate(24,0) scale(-1,1)' : undefined}>
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          d="M8.2 7.6A6.2 6.2 0 1 1 6.1 13.2"
        />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          opacity="0.55"
          d="M10.4 6.4A7.6 7.6 0 1 1 7.8 13.6"
        />
        <path fill="currentColor" d="M8 4.6v4.6L4.4 6.9 8 4.6z" />
        <text
          x="13"
          y="14.2"
          textAnchor="middle"
          fill="currentColor"
          fontSize="6.5"
          fontWeight="700"
          fontFamily="system-ui,sans-serif"
        >
          30
        </text>
      </g>
    </svg>
  )
}

function IconPlay() {
  return (
    <svg className="studio-ico studio-ico-main" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M8 5.5v13l11-6.5L8 5.5z" />
    </svg>
  )
}

function IconPause() {
  return (
    <svg className="studio-ico studio-ico-main" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M7 5h3.5v14H7V5zm6.5 0H17v14h-3.5V5z" />
    </svg>
  )
}

function IconLoop() {
  return (
    <svg className="studio-ico" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17 1l4 4-4 4"
      />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 11V9a4 4 0 0 1 4-4h14"
      />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7 23l-4-4 4-4"
      />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 13v2a4 4 0 0 1-4 4H3"
      />
    </svg>
  )
}

export function StudioPlayer({ src, srcAlt, title, downloadName, showSave = true }: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const barRef = useRef<HTMLDivElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const [loop, setLoop] = useState(false)
  const [current, setCurrent] = useState(0)
  const [duration, setDuration] = useState(0)
  const [error, setError] = useState('')
  const [, setStatus] = useState('Ready')
  const seekingRef = useRef(false)
  const pendingSeek = useRef<number | null>(null)

  const url = mediaUrl(src)
  const urlAlt = srcAlt ? mediaUrl(srcAlt) : ''

  const mediaDuration = useCallback(
    (el: HTMLAudioElement) => {
      if (Number.isFinite(el.duration) && el.duration > 0) return el.duration
      if (Number.isFinite(duration) && duration > 0) return duration
      try {
        if (el.seekable.length > 0) {
          const end = el.seekable.end(el.seekable.length - 1)
          if (Number.isFinite(end) && end > 0) return end
        }
      } catch {
        /* seekable not ready */
      }
      return 0
    },
    [duration],
  )

  const seekTo = useCallback(
    (time: number) => {
      const el = audioRef.current
      if (!el) return
      const max = mediaDuration(el)
      if (!max) {
        pendingSeek.current = Math.max(0, time)
        return
      }
      const next = Math.min(Math.max(0, time), max)
      pendingSeek.current = null
      const resume = !el.paused && !seekingRef.current
      try {
        el.currentTime = next
        setCurrent(next)
      } catch {
        pendingSeek.current = next
        return
      }
      if (resume) {
        el.addEventListener(
          'seeked',
          () => {
            if (el.paused) void el.play().catch(() => {})
          },
          { once: true },
        )
      }
    },
    [mediaDuration],
  )

  const skip = useCallback(
    (delta: number) => {
      const el = audioRef.current
      if (!el) return
      seekTo((el.currentTime || 0) + delta)
    },
    [seekTo],
  )

  const ensureLoud = (el: HTMLAudioElement) => {
    el.muted = false
    try {
      el.volume = 1
    } catch {
      /* ignore */
    }
  }

  const togglePlay = useCallback(async () => {
    const el = audioRef.current
    if (!el) return
    setError('')
    if (el.paused) {
      try {
        ensureLoud(el)
        setStatus('Starting…')
        await el.play()
        setPlaying(true)
        setStatus('Playing')
      } catch (e) {
        setPlaying(false)
        const msg = e instanceof Error ? e.message : String(e)
        setStatus('Blocked')
        setError('Could not start audio. Raise volume and tap Play again, or use Download MP3.')
        console.error('audio play failed', msg)
      }
    } else {
      el.pause()
      setPlaying(false)
      setStatus('Paused')
    }
  }, [])

  useEffect(() => {
    const el = audioRef.current
    if (el) el.loop = loop
  }, [loop])

  useEffect(() => {
    setPlaying(false)
    setCurrent(0)
    setDuration(0)
    setError('')
    setStatus('Ready')
    const el = audioRef.current
    if (el) {
      el.pause()
      el.load()
    }
  }, [url, urlAlt])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'A') return
      if (e.code === 'Space') {
        e.preventDefault()
        void togglePlay()
      } else if (e.code === 'ArrowLeft') {
        e.preventDefault()
        skip(e.shiftKey ? -30 : -10)
      } else if (e.code === 'ArrowRight') {
        e.preventDefault()
        skip(e.shiftKey ? 30 : 10)
      } else if (e.key === 'l' || e.key === 'L') {
        setLoop((v) => !v)
      } else if (e.key === '0' || e.key === 'Home') {
        seekTo(0)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [skip, seekTo, togglePlay])

  function timeFromPointer(clientX: number) {
    const bar = barRef.current
    const el = audioRef.current
    if (!bar || !el) return 0
    const max = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : duration
    if (!max) return 0
    const rect = bar.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    return ratio * max
  }

  function onBarPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    e.preventDefault()
    seekingRef.current = true
    const bar = barRef.current
    if (bar) {
      try {
        bar.setPointerCapture(e.pointerId)
      } catch {
        /* pointer already released */
      }
    }
    seekTo(timeFromPointer(e.clientX))
  }

  function onBarPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (!seekingRef.current) return
    seekTo(timeFromPointer(e.clientX))
  }

  function endScrub(clientX: number) {
    if (!seekingRef.current) return
    seekingRef.current = false
    seekTo(timeFromPointer(clientX))
  }

  const progress = duration > 0 ? (current / duration) * 100 : 0
  const fileName = downloadName || 'AIAS-track.mp3'

  return (
    <div className="studio">
      <div className="studio-head">
        <p className="studio-kicker">Studio stream</p>
        <h2 className="studio-title">{title}</h2>
      </div>

      {/* MP3 is the playback file. The M4A on Pages ignores Range and answers
          200 from byte 0, so a skip or a scrub restarts the song. */}
      <audio
        ref={audioRef}
        key={url}
        src={url}
        playsInline
        preload="auto"
        className="studio-audio-hidden"
        onPlay={() => {
          if (audioRef.current) ensureLoud(audioRef.current)
          setPlaying(true)
          setStatus('Playing')
          setError('')
        }}
        onPause={() => {
          setPlaying(false)
          setStatus('Paused')
        }}
        onPlaying={() => {
          if (audioRef.current) ensureLoud(audioRef.current)
          setPlaying(true)
          setStatus('Playing')
        }}
        onEnded={() => {
          if (!loop) {
            setPlaying(false)
            setStatus('Ended')
          }
        }}
        onTimeUpdate={() => {
          if (seekingRef.current || !audioRef.current) return
          setCurrent(audioRef.current.currentTime)
        }}
        onSeeked={() => {
          if (seekingRef.current || !audioRef.current) return
          setCurrent(audioRef.current.currentTime)
        }}
        onLoadedMetadata={() => {
          const el = audioRef.current
          if (!el || !Number.isFinite(el.duration)) return
          setDuration(el.duration)
          if (pendingSeek.current != null) {
            const queued = pendingSeek.current
            pendingSeek.current = null
            const next = Math.min(Math.max(0, queued), el.duration)
            try {
              el.currentTime = next
              setCurrent(next)
            } catch {
              /* metadata arrived before the file was seekable */
            }
          }
        }}
        onError={() => {
          setError('Stream failed to load. Try Download MP3 or Download M4A below.')
          setStatus('Error')
        }}
      />

      {error ? (
        <p className="studio-error" role="alert">
          {error}
        </p>
      ) : null}

      <div
        className="studio-bar"
        ref={barRef}
        role="slider"
        aria-label="Seek"
        aria-valuemin={0}
        aria-valuemax={Math.floor(duration) || 0}
        aria-valuenow={Math.floor(current)}
        tabIndex={0}
        onPointerDown={onBarPointerDown}
        onPointerMove={onBarPointerMove}
        onPointerUp={(e) => endScrub(e.clientX)}
        onPointerCancel={() => {
          seekingRef.current = false
        }}
      >
        <div className="studio-bar-fill" style={{ width: `${progress}%` }} />
        <div
          className="studio-bar-knob"
          style={{ left: `clamp(9px, ${progress}%, calc(100% - 9px))` }}
        />
      </div>

      <div className="studio-times">
        <span>{formatTime(current)}</span>
        <span>{formatTime(duration)}</span>
      </div>

      <div className="studio-controls" role="group" aria-label="Playback controls">
        <div className="studio-cluster">
          <button type="button" className="studio-btn studio-btn-icon" onClick={() => seekTo(0)} title="Restart" aria-label="Restart">
            <IconRestart />
          </button>
          <button type="button" className="studio-btn studio-btn-icon" onClick={() => skip(-30)} title="Back 30s" aria-label="Back 30 seconds">
            <IconSkip30 dir="back" />
          </button>
          <button type="button" className="studio-btn studio-btn-icon" onClick={() => skip(-10)} title="Back 10s" aria-label="Back 10 seconds">
            <IconSkip10 dir="back" />
          </button>
        </div>
        <button
          type="button"
          className="studio-btn studio-btn-main studio-btn-icon"
          onClick={() => void togglePlay()}
          title={playing ? 'Pause' : 'Play'}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {playing ? <IconPause /> : <IconPlay />}
        </button>
        <div className="studio-cluster">
          <button type="button" className="studio-btn studio-btn-icon" onClick={() => skip(10)} title="Forward 10s" aria-label="Forward 10 seconds">
            <IconSkip10 dir="fwd" />
          </button>
          <button type="button" className="studio-btn studio-btn-icon" onClick={() => skip(30)} title="Forward 30s" aria-label="Forward 30 seconds">
            <IconSkip30 dir="fwd" />
          </button>
          <button
            type="button"
            className={`studio-btn studio-btn-icon ${loop ? 'is-on' : ''}`}
            onClick={() => setLoop((v) => !v)}
            aria-pressed={loop}
            title={loop ? 'Loop on' : 'Loop'}
            aria-label={loop ? 'Loop on' : 'Loop off'}
          >
            <IconLoop />
          </button>
        </div>
      </div>

      {showSave ? (
        <div className="studio-download">
          <a className="btn btn-solid" href={url} download={fileName}>
            Download MP3
          </a>
          {urlAlt ? (
            <a className="btn" href={urlAlt} download={fileName.replace(/\.mp3$/i, '.m4a')}>
              Download M4A
            </a>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
